import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Global from '../../../../store/global-selectors';
import {
  activateEnhancedReturns,
  activateStore,
  getRetailerConfiguration,
  updateEnhancedReturnsStoreStatus,
} from '../../../../store/returns/returns.api';
import { STORE_ACTIVATION_MODE_BASIC } from '../../../../store/returns/returns.helpers';
import { refreshStoreList } from '../../../../store/seller/seller.actions';
import { useIsShipstationReturnsEnabled } from '../../../../hooks/use-seller-setting';
import { messages } from '../messages';

const MAX_ACTIVATION_ATTEMPTS = 2;
const MAX_POLL_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2500;

type EnsureStoreActivatedResult = {
  isReady: boolean;
  isActivating: boolean;
  error: string | null;
};

export function useEnsureStoreActivated(storeGuid: string | undefined): EnsureStoreActivatedResult {
  const intl = useIntl();
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const store = useSelector((state) => Global.selectSellerStoreData(state, { storeGuid }));
  const dispatch = useDispatch();

  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasActivated, setHasActivated] = useState(false);
  const attemptCount = useRef(0);

  const needsActivation = isShipstationReturnsEnabled && !store?.isStoreSetupCompleteForEnhancedReturns && !hasActivated;

  const pollStoreProperties = useCallback((guid: string): Promise<boolean> => {
    const poll = (attempt: number): Promise<boolean> =>
      getRetailerConfiguration(guid).then((config) => {
        if (config?.storeProperties) return true;
        if (attempt >= MAX_POLL_ATTEMPTS - 1) return false;
        return new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS)).then(() => poll(attempt + 1));
      });
    return poll(0);
  }, []);

  const attemptActivation = useCallback(async () => {
    if (!storeGuid) return;

    setIsActivating(true);
    setError(null);

    try {
      await activateEnhancedReturns();
      await activateStore(storeGuid, { mode: STORE_ACTIVATION_MODE_BASIC });
      await updateEnhancedReturnsStoreStatus(storeGuid);
      await pollStoreProperties(storeGuid);
      dispatch(refreshStoreList());
      setHasActivated(true);
    } catch (err: unknown) {
      attemptCount.current += 1;
      if (attemptCount.current < MAX_ACTIVATION_ATTEMPTS) {
        attemptActivation();
        return;
      }
      const axiosError = err as { response?: { data?: { Errors?: Array<{ Message?: string }> } } };
      const errorMessage = axiosError?.response?.data?.Errors?.[0]?.Message ?? intl.formatMessage(messages.activationErrorFallback);
      setError(errorMessage);
    } finally {
      setIsActivating(false);
    }
  }, [storeGuid, dispatch, intl, pollStoreProperties]);

  useEffect(() => {
    if (needsActivation && attemptCount.current === 0) {
      attemptActivation();
    }
  }, [needsActivation, attemptActivation]);

  if (!needsActivation) {
    return { isReady: true, isActivating: false, error: null };
  }

  return { isReady: hasActivated, isActivating, error };
}
